// Utility functions extracted from the original interview.html

/**
 * Sanitize HTML content to prevent XSS attacks
 * This is a simplified version - consider using a library like DOMPurify for production
 */
export const sanitizeHTML = (html: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Remove script tags and other dangerous elements
  const scripts = temp.querySelectorAll('script, iframe, object, embed');
  scripts.forEach(script => script.remove());

  // Remove event handlers
  const allElements = temp.querySelectorAll('*');
  allElements.forEach(element => {
    const attributes = element.attributes;
    for (let i = attributes.length - 1; i >= 0; i--) {
      const attr = attributes[i];
      if (attr.name.startsWith('on')) {
        element.removeAttribute(attr.name);
      }
    }
  });

  return temp.innerHTML;
};

/**
 * Format analysis results with basic markdown to HTML conversion
 */
export const formatAnalysisResults = (text: string): string => {
  let html = text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>');
  return html.replace(/\n\n/g, '<br><br>').replace(/\n/g, '<br>');
};

/**
 * Validate email address format
 */
export const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * Validate Application ID format (basic validation)
 */
export const validateApplicationId = (id: string): boolean => {
  // Basic validation - adjust regex as needed based on Salesforce ID format
  const trimmedId = id.trim();
  return trimmedId.length >= 15 && /^[a-zA-Z0-9]+$/.test(trimmedId);
};

/**
 * Get URL parameter by name
 */
export const getUrlParameter = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

/**
 * Scroll element to bottom, but only if user is already near the bottom.
 * This prevents jumping when the user is reading older messages.
 */
export const scrollToBottom = (element: HTMLElement, threshold: number = 120): void => {
  const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
  if (distanceFromBottom <= threshold) {
    // User is close to the bottom – safe to autoscroll
    element.scrollTop = element.scrollHeight;
  }
};

/**
 * Debounce function calls
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleTimeString();
};

/**
 * Generate a unique ID
 */
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Extract candidate name from analysis text
 */
export const extractCandidateName = (analysis: string): string => {
  const nameMatch = analysis.match(/([A-Z][a-z]+),\s+I\s+observed/);
  return nameMatch && nameMatch[1] ? nameMatch[1] : "there";
};

/**
 * Generate unique ID for messages
 */
export const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validate application ID format
 */
export const isValidApplicationId = (id: string): boolean => {
  // Basic validation - adjust pattern as needed
  const pattern = /^[a-zA-Z0-9]{15,18}$/;
  return pattern.test(id.trim());
};

/**
 * Check if text contains HTML tags
 */
export const containsHTML = (text: string): boolean => {
  const htmlRegex = /<[^>]*>/;
  return htmlRegex.test(text);
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Check if running in development mode
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Check if running in production mode
 */
export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

/**
 * Check if running in Render environment
 */
export const isRenderEnvironment = (): boolean => {
  return window.location.hostname.includes('onrender.com') ||
         window.location.hostname === 'isa-interviewer-backend.onrender.com';
};

export const formatJobDescription = (text: string): string => {
  if (!text) return '';

  // Pre-process the text to fix the broken "1 \n\n 0." into "10."
  const preProcessedText = text.replace(/(\n\s*)1\s*\n\s*0\./g, '$110.');

  const lines = preProcessedText.split('\n').filter(line => line.trim() !== '');
  let html = '';
  let inList = false;
  let listType: 'ol' | 'ul' = 'ul';

  lines.forEach(line => {
    const trimmedLine = line.trim();
    const sectionMatch = trimmedLine.match(/^(\d+\.\s[^:]+):/);

    if (sectionMatch) {
      if (inList) {
        html += `</${listType}>`;
        inList = false;
      }

      let title = sectionMatch[1];
      if (title.includes('(')) {
        title = title.substring(0, title.indexOf('(')).trim();
      }
      html += `<h2>${title}</h2>`;

      const contentAfterTitle = trimmedLine.substring(sectionMatch[0].length).trim();

      if (title.includes('Responsibilities')) {
        inList = true;
        listType = 'ol';
        html += `<ol>`;
      } else if (title.includes('Requirements') || title.includes('Qualifications') || title.includes('Competencies')) {
        inList = true;
        listType = 'ul';
        html += `<ul>`;
      }

      if (contentAfterTitle) {
        if (inList) {
          const itemContent = contentAfterTitle.replace(/^\d+\.\s/, '').replace(/^(?:Requirement|Qualification)\s\d+:\s*|^\s*-\s*/, '');
          html += `<li>${itemContent}</li>`;
        } else if (title.includes('Compensation')) {
            const compContent = contentAfterTitle.replace('Job Description Salary Range:', '<strong>Salary Range:</strong>').replace('Job Description Benefits:', '<br><strong>Benefits:</strong>');
            html += `<p>${compContent}</p>`;
        } else {
            html += `<p>${contentAfterTitle}</p>`;
        }
      }

    } else if (inList) {
      const itemContent = trimmedLine.replace(/^\d+\.\s/, '').replace(/^(?:Requirement|Qualification)\s\d+:\s*|^\s*-\s*/, '');
      html += `<li>${itemContent}</li>`;
    } else {
      html += `<br>${trimmedLine}`;
    }
  });

  if (inList) {
    html += `</${listType}>`;
  }

  return html;
};
